import { IntervalUnit } from '@types';
import { intervalDict } from './historianUtils';

// Enum for interval units to avoid magic strings
export enum IntervalUnitEnum {
  MILLISECOND = 'millisecond',
  SECOND = 'second',
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  MONTH = 'month',
}

// Array of all interval units for iteration (explicit order to ensure consistency)
export const INTERVAL_UNITS: IntervalUnit[] = [
  IntervalUnitEnum.MILLISECOND,
  IntervalUnitEnum.SECOND,
  IntervalUnitEnum.MINUTE,
  IntervalUnitEnum.HOUR,
  IntervalUnitEnum.DAY,
  IntervalUnitEnum.MONTH,
];

export interface TimeRangeInfo {
  isValid: boolean;
  rangeMs: number;
}

export interface IntervalCalculationParams {
  timeRangeMs: number;
  intervalValue: number;
  intervalUnit: IntervalUnit;
  dataPointsCount: number;
}

export const calculateTotalPoints = ({
  timeRangeMs,
  intervalValue,
  intervalUnit,
  dataPointsCount,
}: IntervalCalculationParams): number => {
  const intervalMs = intervalValue * intervalDict[intervalUnit] * 1000;
  const pointsPerDatapoint = Math.ceil(timeRangeMs / intervalMs);
  const actualDataPointsCount = Math.max(dataPointsCount, 1);
  return pointsPerDatapoint * actualDataPointsCount;
};

export const validateTimeRange = (
  startDateTime: unknown,
  stopDateTime: unknown,
): TimeRangeInfo => {
  if (!startDateTime || !stopDateTime) {
    return { isValid: false, rangeMs: 0 };
  }

  // Type guard to ensure we have objects with valueOf method
  if (
    typeof startDateTime !== 'object' ||
    typeof stopDateTime !== 'object' ||
    !('valueOf' in startDateTime) ||
    !('valueOf' in stopDateTime) ||
    typeof startDateTime.valueOf !== 'function' ||
    typeof stopDateTime.valueOf !== 'function'
  ) {
    return { isValid: false, rangeMs: 0 };
  }

  const start = Number(startDateTime.valueOf());
  const stop = Number(stopDateTime.valueOf());

  // Validate that the values are finite numbers
  if (!Number.isFinite(start) || !Number.isFinite(stop)) {
    return { isValid: false, rangeMs: 0 };
  }

  const rangeMs = stop - start;

  return {
    isValid: rangeMs > 0,
    rangeMs,
  };
};

export const getValidIntervalOptions = (
  timeRangeMs: number,
  intervalValue: number,
  dataPointsCount: number,
  pointsLimit: number,
): IntervalUnit[] => {
  if (timeRangeMs <= 0 || !intervalValue) {
    return INTERVAL_UNITS;
  }

  // Calculate total points for each interval unit and filter based on points limit
  return INTERVAL_UNITS.filter((unit) => {
    const totalPoints = calculateTotalPoints({
      timeRangeMs,
      intervalValue,
      intervalUnit: unit,
      dataPointsCount,
    });
    return totalPoints <= pointsLimit;
  });
};

export const findOptimalIntervalUnit = (
  timeRangeMs: number,
  intervalValue: number,
  dataPointsCount: number,
  pointsLimit: number,
  currentUnit: IntervalUnit,
): IntervalUnit | null => {
  const currentPoints = calculateTotalPoints({
    timeRangeMs,
    intervalValue,
    intervalUnit: currentUnit,
    dataPointsCount,
  });

  if (currentPoints <= pointsLimit) {
    return null;
  }

  for (const unit of INTERVAL_UNITS) {
    const totalPoints = calculateTotalPoints({
      timeRangeMs,
      intervalValue,
      intervalUnit: unit,
      dataPointsCount,
    });

    if (totalPoints <= pointsLimit) {
      return unit;
    }
  }

  return null;
};
